import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Search, Loader2, Zap, Clock } from 'lucide-react'

interface SearchFormProps {
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  searchType: 'basic' | 'deep'
  onSearchTypeChange: (value: 'basic' | 'deep') => void
  onSubmit: (e: React.FormEvent) => void
  isLoading: boolean
}

export function SearchForm({
  searchQuery,
  onSearchQueryChange,
  searchType,
  onSearchTypeChange,
  onSubmit,
  isLoading
}: SearchFormProps) {
  return (
    <TooltipProvider>
      <div>
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                <Search className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">Search Parameters</CardTitle>
                <CardDescription>
                  Describe the influencers you're looking for
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="search-query">Search Query</Label>
                <Textarea
                  id="search-query"
                  value={searchQuery}
                  onChange={(e) => onSearchQueryChange(e.target.value)}
                  placeholder="Mediterranean cooking channels and influencers"
                  className="min-h-[100px] resize-none"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Adding location does not restrict the search to that location, but it boosts the results in that demographic.
                </p>
              </div>

              <div className="space-y-3">
                <Label>Search Type</Label>
                <RadioGroup 
                  value={searchType} 
                  onValueChange={(value: 'basic' | 'deep') => onSearchTypeChange(value)}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="basic" id="basic" />
                    <div className="flex items-center gap-2 flex-1">
                      <Zap className="h-3 w-3 text-green-600" />
                      <Label htmlFor="basic" className="text-sm font-normal flex-1">
                        <span className="font-medium">Basic Search</span> - Fast results from profile data
                      </Label>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="deep" id="deep" />
                    <div className="flex items-center gap-2 flex-1">
                      <Clock className="h-3 w-3 text-purple-600" />
                      <Label htmlFor="deep" className="text-sm font-normal flex-1">
                        <span className="font-medium">Deep Search</span> - Detailed analysis including recent content
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  type="submit" 
                  disabled={!searchQuery.trim() || isLoading}
                  className="flex-1"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Search Influencers
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  )
} 